import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Globe, ChevronDown, Check } from "lucide-react";

const LANGS = [
  { code: "nl", label: "Nederlands", short: "NL", flag: "🇳🇱" },
  { code: "en", label: "English",    short: "EN", flag: "🇬🇧" },
  { code: "hu", label: "Magyar",     short: "HU", flag: "🇭🇺" },
];

function stripLangSegment(pathname) {
  const seg = pathname.split("/").filter(Boolean);
  if (seg[0] && ["nl", "en", "hu"].includes(seg[0])) {
    return "/" + seg.slice(1).join("/");
  }
  return pathname;
}

export default function LanguageSwitcher({ variant = "dark", compact = false, onNavigate, testIdSuffix = "" }) {
  const { i18n } = useTranslation();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const active = LANGS.find((l) => l.code === i18n.language) || LANGS[0];

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const change = (code) => {
    // Full page load on language switch — guarantees third-party widgets
    // (Trustoo score / reviews) hydrate cleanly and Google Ads fires a fresh
    // 'config' + page_view against the new URL.
    const base = stripLangSegment(loc.pathname) || "/";
    const rest = base === "/" ? "" : base;
    const target = `/${code}${rest}${loc.hash || ""}`;
    setOpen(false);
    if (onNavigate) onNavigate();
    // Persist the choice before reload so the detector agrees on first paint
    try { localStorage.setItem("i18nextLng", code); } catch (_) { /* ignore */ }
    window.location.assign(target);
  };

  const isDark = variant === "dark";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`lang-switcher-btn${testIdSuffix}`}
        className={`inline-flex items-center gap-2 px-3 py-2 font-bold uppercase tracking-wider text-xs transition-colors border ${
          isDark
            ? "text-white border-white/15 hover:border-[#FF5A1F] bg-white/5"
            : "text-[#0A1128] border-slate-300 hover:border-[#0A1128] bg-white"
        }`}
      >
        <span className="text-base leading-none" aria-hidden>{active.flag}</span>
        <span className="hidden sm:inline">{active.short}</span>
        <Globe className="w-3.5 h-3.5 sm:hidden" />
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid={`lang-switcher-menu${testIdSuffix}`}
          className={`absolute right-0 mt-2 w-48 z-50 shadow-2xl border ${
            isDark ? "bg-[#0A1128] border-white/10" : "bg-white border-slate-200"
          }`}
        >
          {LANGS.map((l) => {
            const isActive = l.code === active.code;
            return (
              <button
                key={l.code}
                onClick={() => change(l.code)}
                data-testid={`lang-option-${l.code}`}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  isDark
                    ? isActive
                      ? "bg-white/5 text-white"
                      : "text-white/80 hover:bg-white/5 hover:text-white"
                    : isActive
                      ? "bg-slate-50 text-[#0A1128]"
                      : "text-slate-700 hover:bg-slate-50 hover:text-[#0A1128]"
                }`}
              >
                <span className="text-lg leading-none">{l.flag}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold uppercase tracking-wider">{l.short}</div>
                  <div className={`text-[11px] uppercase tracking-widest ${isDark ? "text-white/50" : "text-slate-500"}`}>
                    {l.label}
                  </div>
                </div>
                {isActive && <Check className={`w-4 h-4 ${isDark ? "text-[#FF5A1F]" : "text-[#FF5A1F]"}`} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
